import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import Flicking, { ChangedEvent } from '@egjs/react-flicking';
import { MOVE_TYPE } from '@egjs/flicking';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { GeneralImageZoom, Tooltip, UserContext } from '@/components';
import { useFullscreen } from '@/utils';
import { UserRole } from '@/types';

import CornersOutLight from '@/assets/cornersOutLight.svg';
import FullscreenExit from '@/assets/fullscreenExit.svg';

import { MediaGalleryImageModel } from './MediaGalleryImagesList';
import MediaGalleryCarouselPaginator from './MediaGalleryCarouselPaginator';

interface MediaGalleryRightSideProps {
  imageList: MediaGalleryImageModel[];
  activeImageIndex: number;
  setActiveImage: (image: MediaGalleryImageModel) => void;
}

export interface MediaGalleryRightSideMethods {
  carouselNext: () => void;
  carouselPrev: () => void;
}

const MediaGalleryRightSide = forwardRef<MediaGalleryRightSideMethods, MediaGalleryRightSideProps>(
  ({ imageList, activeImageIndex, setActiveImage }, ref) => {
    const carouselRef = useRef<Flicking>(null);
    const mainElementRef = useRef<HTMLDivElement>(null);

    const t = useTranslations();
    const { roles } = useContext(UserContext);
    const { isFullscreen, exitFullscreen, enterFullscreen } = useFullscreen();

    const showCopyBtns = useMemo(() => roles?.includes(UserRole.Staff) ?? false, [roles]);

    const widths = useMemo(
      () => ({
        xs: 960,
        sm: 1800,
        md: 2000,
        lg: 2500,
        xl: 3000,
        xxl: 4000,
      }),
      [],
    );

    const carouselNext = useCallback(() => {
      carouselRef.current?.next();
    }, []);

    const carouselPrev = useCallback(() => {
      carouselRef.current?.prev();
    }, []);

    useImperativeHandle(ref, () => ({ carouselNext, carouselPrev }), [carouselNext, carouselPrev]);

    const onCarouselChanged = useCallback(
      (e: ChangedEvent<Flicking>) => {
        const { index } = e.currentTarget;
        const image = imageList[index];
        if (image) setActiveImage(image);
      },
      [imageList, setActiveImage],
    );

    const moveToIndex = useCallback(
      (index: number | null | undefined) => {
        const flicking = carouselRef.current;
        if (!flicking) return;
        if (index === null || index === undefined) return;
        if (flicking.index === index) return;
        if (flicking.animating) return;
        if (index < 0 || index >= imageList.length) return;

        flicking.moveTo(index);
      },
      [imageList.length],
    );

    useEffect(() => {
      moveToIndex(activeImageIndex);
    }, [activeImageIndex, moveToIndex]);

    const zoomActivated = useCallback(() => {
      carouselRef.current?.disableInput();
    }, []);

    const zoomDeactivated = useCallback(() => {
      carouselRef.current?.enableInput();
    }, []);

    const openFullscreen = useCallback(() => {
      if (mainElementRef.current) enterFullscreen(mainElementRef.current);
    }, [enterFullscreen]);

    const fullscreenButton = useMemo(
      () => ({
        icon: <CornersOutLight className="h-6 w-6 !fill-black" />,
        tooltipText: t('imageGallery.$openFullscreen'),
        onClick: openFullscreen,
      }),
      [openFullscreen, t],
    );

    return (
      <div
        ref={mainElementRef}
        className={classNames(
          'relative flex h-full w-full flex-col justify-center gap-2',
          isFullscreen ? '[&_.copyright]:hidden [&_.general-image]:mb-0' : 'py-3 lg:py-4',
        )}
      >
        <Flicking
          ref={carouselRef}
          moveType={[MOVE_TYPE.STRICT, { count: 1 }]}
          interruptable
          align="center"
          panelsPerView={1}
          preventClickOnDrag
          preventDefaultOnDrag
          defaultIndex={activeImageIndex}
          onChanged={onCarouselChanged}
          bounce={imageList.length > 1 ? '20%' : 0}
          dragThreshold={5}
          className="h-full"
        >
          {imageList.map((image) => {
            const isVisible =
              image.index === activeImageIndex + 1 ||
              image.index === activeImageIndex ||
              image.index === activeImageIndex - 1;

            return (
              <div
                key={image.index}
                className="h-full [&_.responsive-image]:h-full [&_.responsive-image_img]:object-contain"
              >
                {isVisible && (
                  <GeneralImageZoom
                    image={image}
                    widths={widths}
                    visible={isVisible}
                    showCopyrightText
                    disableClick
                    setContanerSize={!isFullscreen}
                    dataNotLazy={isVisible}
                    zoomActivated={zoomActivated}
                    zoomDeactivated={zoomDeactivated}
                    buttons={isFullscreen ? undefined : [fullscreenButton]}
                    showCopyBtns={showCopyBtns}
                  />
                )}
              </div>
            );
          })}
        </Flicking>

        {!isFullscreen && imageList.length > 1 && (
          <div className="flex justify-center lg:hidden">
            <MediaGalleryCarouselPaginator
              imageList={imageList}
              activeImageIndex={activeImageIndex}
              carouselPrev={carouselPrev}
              carouselNext={carouselNext}
            />
          </div>
        )}

        {isFullscreen && (
          <div className="absolute right-3 bottom-3 z-10">
            <Tooltip placement="left" content={t('imageGallery.$exitFullscreen')}>
              <button
                className="btn icon cursor-pointer rounded-full p-2 shadow-md transition-all"
                onClick={exitFullscreen}
              >
                <FullscreenExit className="h-6 w-6" />
              </button>
            </Tooltip>
          </div>
        )}
      </div>
    );
  },
);

MediaGalleryRightSide.displayName = 'MediaGalleryRightSide';
export default MediaGalleryRightSide;
